import React, { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import MountainLogo from "../assets/mountain-logo.png";
import HelionLogo from "../assets/helion-logo.png";
import ThemeToggle from "./ui/ThemeToggle";
import MarketChimes from "./MarketChimes";
import FundGuardBanner from "./FundGuardBanner";

type LayoutProps = {
  children: ReactNode;
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();

  const isActive = (path: string) => {
    if (path === "/leaps") {
      return location.pathname === "/" || location.pathname === "/leaps";
    }
    return location.pathname === path;
  };

  const navLinkStyle = (active: boolean): React.CSSProperties => ({
    color: active ? "var(--nav-hover)" : "var(--nav-text)",
    textDecoration: "none",
    fontWeight: 600,
    fontSize: "0.95rem",
    transition: "color 160ms ease",
    whiteSpace: "nowrap",
  });

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        backgroundColor: "var(--bg-primary)",
        color: "var(--text-primary)",
      }}
    >
      <nav
        className="shadow-md px-4 py-3 flex items-center justify-between"
        style={{
          backgroundColor: "var(--nav-bg)",
          color: "var(--nav-text)",
        }}
      >
        <div className="flex items-center min-w-[120px]">
          <Link to="/leaps" style={{ display: "inline-flex", alignItems: "center" }}>
            <img
              src={MountainLogo}
              alt="Helion Mountain Logo"
              className="h-8 w-auto"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </Link>
        </div>

      {/* The eight page links used to sit in a single `flex gap-6` with no wrap, no
          shrink and no scroll, so below ~1180px they pushed the document wider than the
          viewport — every page scrolled sideways at 1024px (106px of overflow), not just
          this one. Fix is two-part and deliberately not a redesign: tighten the rhythm at
          intermediate widths, then let the link row scroll INSIDE itself as the backstop.
          No destination is ever hidden, and the body can no longer overflow at any width.
          The utility cluster stays outside the scroll area so the bell and theme switch
          are always reachable. */}
        <div className="flex items-center gap-3 xl:gap-6 min-w-0">
        <div className="flex items-center gap-3 lg:gap-4 xl:gap-6 min-w-0 overflow-x-auto whitespace-nowrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <Link to="/leaps" style={navLinkStyle(isActive("/leaps"))}>
            Options
          </Link>
          <Link to="/trading-desk" style={navLinkStyle(isActive("/trading-desk"))}>
            Trading Desk
          </Link>
          <Link to="/fixed-income" style={navLinkStyle(isActive("/fixed-income"))}>
            Fixed Income
          </Link>
          <Link to="/portfolio" style={navLinkStyle(isActive("/portfolio"))}>
            Portfolio
          </Link>
          <Link to="/risk" style={navLinkStyle(isActive("/risk"))}>
            Risk Dashboard
          </Link>
          <Link to="/market" style={navLinkStyle(isActive("/market"))}>
            Market Snapshot
          </Link>
          <Link to="/screener" style={navLinkStyle(isActive("/screener"))}>
            Options Screener
          </Link>
          <Link to="/economics" style={navLinkStyle(isActive("/economics"))}>
            Economics
          </Link>
        </div>
          {/* Utility cluster. Tight internal spacing on purpose: the parent's gap-6 is
              the rhythm of the PAGE LINKS, and inheriting it made the bell and the theme
              switch read as two more destinations. Grouping them at 6px apart while the
              parent keeps them 24px clear of "Economics" makes them one secondary
              control group, subordinate to the navigation. It now also sits OUTSIDE the
              scrollable link row, so these controls never scroll out of reach. */}
          <div className="flex items-center gap-1.5 shrink-0">
            <MarketChimes />
            <ThemeToggle />
          </div>
        </div>
      </nav>

      <main
        className="flex-grow px-4" //This adjusts the margins on the pages
        style={{
          backgroundColor: "var(--bg-primary)",
          color: "var(--text-primary)",
        }}
      >
        {/* Fund blend guard (2026-09-16; M3 Decision 7 2026-10-28): red for money in an account not on the
            fund list, grey when a registered fund other than Horizon holds money, amber when unverified. */}
        <FundGuardBanner />
        {children}
      </main>

      <footer
        className="py-2 flex justify-center items-center"
        style={{
          backgroundColor: "var(--footer-bg)",
        }}
      >
        <img
          src={HelionLogo}
          alt="Helion Footer Logo"
          className="h-16 w-auto opacity-90"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      </footer>
    </div>
  );
};

export default Layout;